<template>
  <div class="container">
    <div class="columns box-title is-12">
      <div class="column is-12">
        <h4 class="has-text-left">
          <i class="fal fa-trash-alt"></i>
          {{ $t('pddDispose.page_title') }}
        </h4>
        <p class="hint">{{ $t('pddDispose.page_hint') }}</p>
      </div>
    </div>

    <!-- Waiting for PDD's instruction -->
    <div class="box">
      <div class="section-head">
        <h5>{{ $t('pddDispose.awaiting_title') }}</h5>
        <button class="button is-small" :class="{'is-loading': listLoading}" :disabled="listLoading" @click="loadList">
          <i class="fal fa-sync"></i>&nbsp;{{ $t('pddDispose.reload') }}
        </button>
      </div>
      <p class="hint">{{ $t('pddDispose.awaiting_hint') }}</p>

      <div v-if="listLoading && !loaded" class="state-panel"><Loading/></div>

      <div v-else-if="listError" class="state-panel error-panel">
        <p>{{ listError }}</p>
        <button class="button is-small" @click="loadList">{{ $t('pddDispose.retry') }}</button>
      </div>

      <p v-else-if="!available" class="notification is-warning is-light mt-2">{{ $t('pddDispose.not_available') }}</p>

      <p v-else-if="loaded && !rows.length" class="has-text-grey has-text-centered py-3">{{ $t('pddDispose.empty') }}</p>

      <div v-else-if="rows.length" class="table-container mt-2">
        <table class="table is-fullwidth is-striped is-hoverable">
          <thead>
          <tr>
            <th>{{ $t('pddDispose.columns.waybill_code') }}</th>
            <th>{{ $t('pddDispose.columns.arrived_at') }}</th>
            <th class="has-text-right">{{ $t('pddDispose.columns.warehouse_day') }}</th>
            <th class="has-text-right">{{ $t('pddDispose.columns.storage_fee_days') }}</th>
            <th class="has-text-right">{{ $t('pddDispose.columns.extra_fee_total') }}</th>
            <th class="has-text-right">{{ $t('pddDispose.columns.redelivery_count') }}</th>
            <th class="has-text-right">{{ $t('pddDispose.columns.reroute_count') }}</th>
            <th></th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="r in rows" :key="r.waybill_code">
            <td><strong>{{ r.waybill_code }}</strong></td>
            <td>{{ formatDateTime(r.warehouse_arrived_at) }}</td>
            <td class="has-text-right">{{ formatNumber(r.warehouse_day) }}</td>
            <td class="has-text-right">{{ formatNumber(r.storage_fee_days) }}</td>
            <td class="has-text-right">{{ formatMoney(r.extra_fee_total) }}</td>
            <td class="has-text-right">{{ formatNumber(r.redelivery_count) }}</td>
            <td class="has-text-right">{{ formatNumber(r.reroute_count) }}</td>
            <td class="has-text-right">
              <button class="button is-small dispose-button" :disabled="submitting" @click="openDialog(r.waybill_code, r)">
                <i class="fal fa-trash-alt"></i>&nbsp;{{ $t('pddDispose.dispose') }}
              </button>
            </td>
          </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Any other PDD bill: PDD can instruct before day 30 -->
    <div class="box">
      <h5 class="section-title">{{ $t('pddDispose.other_title') }}</h5>
      <p class="hint">{{ $t('pddDispose.other_hint') }}</p>
      <div class="add-row">
        <div class="add-field">
          <label class="label">{{ $t('pddDispose.bill_label') }}:</label>
          <input
              v-model="otherBill"
              class="input"
              type="text"
              maxlength="64"
              :placeholder="$t('pddDispose.bill_placeholder')"
              @keyup.enter="openOther"
          />
          <p class="has-text-danger is-size-7">{{ otherBillError }}</p>
        </div>
        <button class="button add-button dispose-button" :disabled="submitting" @click="openOther">
          <i class="fal fa-trash-alt"></i>&nbsp;{{ $t('pddDispose.dispose') }}
        </button>
      </div>
    </div>

    <!-- Dispose on PDD's instruction -->
    <vs-dialog v-model="dialogOpen" prevent-close :not-close="submitting">
      <div v-if="target" class="card-form">
        <h5 class="dialog-title">{{ $t('pddDispose.dialog_title') }}</h5>
        <div class="bill-box">{{ target.waybill_code }}</div>
        <p v-if="target.row" class="has-text-grey is-size-7">
          {{ $t('pddDispose.dialog_row_summary', {day: formatNumber(target.row.warehouse_day), fee: formatMoney(target.row.extra_fee_total)}) }}
        </p>

        <div class="field-block">
          <label class="label">{{ $t('pddDispose.instruction_label') }} <span class="has-text-danger">*</span></label>
          <input
              v-model="instructionRef"
              class="input"
              type="text"
              :maxlength="refMax"
              :placeholder="$t('pddDispose.instruction_placeholder')"
              :disabled="submitting"
          />
          <p class="hint">{{ $t('pddDispose.instruction_hint') }}</p>
          <p class="has-text-danger is-size-7">{{ instructionError }}</p>
        </div>

        <div class="field-block">
          <label class="label">{{ $t('pddDispose.remark_label') }}</label>
          <textarea
              v-model="remark"
              class="textarea has-fixed-size"
              rows="2"
              :maxlength="remarkMax"
              :placeholder="$t('pddDispose.remark_placeholder')"
              :disabled="submitting"
          ></textarea>
          <p class="has-text-danger is-size-7">{{ remarkError }}</p>
        </div>

        <label class="checkbox confirm-check">
          <input v-model="confirmed" type="checkbox" :disabled="submitting"/>
          {{ $t('pddDispose.confirm_label') }}
        </label>

        <div v-if="dialogError" class="notification is-danger is-light dialog-error">
          <p>{{ dialogError }}</p>
          <p v-if="dialogErrorRetry" class="retry-note">{{ $t('pddDispose.refused_retry') }}</p>
        </div>

        <div class="dialog-actions">
          <button class="button" :disabled="submitting" @click="dialogOpen = false">{{ $t('pddDispose.cancel') }}</button>
          <button class="button dispose-button" :class="{'is-loading': submitting}" :disabled="!canSubmit" @click="submit">
            <i class="fal fa-trash-alt"></i>&nbsp;{{ $t('pddDispose.submit') }}
          </button>
        </div>
      </div>
    </vs-dialog>
  </div>
</template>

<script>
import Loading from '../../Loading';
import {disposeFailure, disposeWaybill, errorMessage, fetchAwaitingInstruction} from '@/services/pddDisposeService';
import {error as notifyError, success as notifySuccess} from '@/Plugins/Notification';

// Same rules as the server's PddDisposeWaybillRequest (counted in characters, after trim).
const REF_MIN = 3;
const REF_MAX = 191;
const REMARK_MAX = 500;
// The API route only matches these characters in a bill number.
const BILL_PATTERN = /^[A-Za-z0-9-]+$/;
// The bill's current step on a 409 → its Lao/CN name; an unknown step is shown as it came.
const TERMINAL_STEPS = ['disposed', 'success', 'successfully_compensated'];

const charCount = (value) => Array.from(value || '').length;

export default {
  name: 'PddDispose',
  components: {Loading},
  data() {
    return {
      rows: [],
      available: true,
      loaded: false,
      listLoading: false,
      listError: '',
      listRequestId: 0,
      otherBill: '',
      otherBillError: '',
      target: null,
      dialogOpen: false,
      instructionRef: '',
      remark: '',
      confirmed: false,
      serverInstructionError: '',
      serverRemarkError: '',
      dialogError: '',
      dialogErrorRetry: false,
      submitting: false,
      refMax: REF_MAX,
      remarkMax: REMARK_MAX,
      alive: true,
    };
  },
  computed: {
    placeholder() {
      return '—';
    },
    trimmedRef() {
      return (this.instructionRef || '').trim();
    },
    trimmedRemark() {
      return (this.remark || '').trim();
    },
    clientInstructionError() {
      const length = charCount(this.trimmedRef);
      if (!length) return '';
      if (length < REF_MIN) return this.$t('pddDispose.instruction_too_short', {min: REF_MIN});
      if (length > REF_MAX) return this.$t('pddDispose.instruction_too_long', {max: REF_MAX});
      return '';
    },
    clientRemarkError() {
      return charCount(this.trimmedRemark) > REMARK_MAX ? this.$t('pddDispose.remark_too_long', {max: REMARK_MAX}) : '';
    },
    instructionError() {
      return this.clientInstructionError || this.serverInstructionError;
    },
    remarkError() {
      return this.clientRemarkError || this.serverRemarkError;
    },
    canSubmit() {
      const length = charCount(this.trimmedRef);
      return !this.submitting
          && !!this.target
          && this.confirmed
          && length >= REF_MIN
          && length <= REF_MAX
          && !this.clientRemarkError;
    },
  },
  watch: {
    instructionRef() {
      this.serverInstructionError = '';
    },
    remark() {
      this.serverRemarkError = '';
    },
    otherBill() {
      this.otherBillError = '';
    },
  },
  methods: {
    async loadList() {
      const requestId = ++this.listRequestId;
      this.listLoading = true;
      this.listError = '';
      try {
        const {available, rows} = await fetchAwaitingInstruction();
        if (requestId !== this.listRequestId || !this.alive) return;
        this.available = available;
        this.rows = rows;
        this.loaded = true;
      } catch (e) {
        if (requestId !== this.listRequestId || !this.alive) return;
        this.listError = errorMessage(e, this.$t('pddDispose.error_loading'));
      } finally {
        if (requestId === this.listRequestId && this.alive) this.listLoading = false;
      }
    },
    openOther() {
      const bill = (this.otherBill || '').trim().toUpperCase();
      if (!bill || !BILL_PATTERN.test(bill)) {
        this.otherBillError = this.$t(bill ? 'pddDispose.bill_invalid' : 'pddDispose.bill_required');
        return;
      }
      // The same bill may already be in the list above — show its numbers in the dialog too.
      this.openDialog(bill, this.rows.find((r) => String(r.waybill_code).toUpperCase() === bill) || null);
    },
    openDialog(waybillCode, row) {
      if (this.submitting) return;
      this.target = {waybill_code: waybillCode, row: row || null};
      this.instructionRef = '';
      this.remark = '';
      this.confirmed = false;
      this.serverInstructionError = '';
      this.serverRemarkError = '';
      this.dialogError = '';
      this.dialogErrorRetry = false;
      this.dialogOpen = true;
    },
    async submit() {
      if (!this.canSubmit) return;
      const target = this.target;
      const fromOther = (this.otherBill || '').trim().toUpperCase() === target.waybill_code;
      this.submitting = true;
      this.dialogError = '';
      this.dialogErrorRetry = false;
      try {
        await disposeWaybill(target.waybill_code, this.trimmedRef, this.trimmedRemark);
        if (!this.alive) return;
        this.notify('success', this.$t('pddDispose.disposed', {bill: target.waybill_code}));
        this.dialogOpen = false;
        if (fromOther) this.otherBill = '';
      } catch (e) {
        if (!this.alive) return;
        this.handleFailure(disposeFailure(e), target);
      } finally {
        if (this.alive) {
          this.submitting = false;
          // Success or not, re-read the list: a refused dispose still keeps the instruction on file.
          this.loadList();
        }
      }
    },
    handleFailure(failure, target) {
      switch (failure.kind) {
        case 'not_pdd_bill':
          this.dialogOpen = false;
          this.notify('danger', this.$t('pddDispose.not_pdd_bill', {bill: target.waybill_code}));
          break;
        case 'already_terminal':
          this.dialogOpen = false;
          this.notify('danger', this.$t('pddDispose.already_terminal', {bill: target.waybill_code, step: this.stepName(failure.step)}));
          break;
        case 'dispose_refused':
          // The dialog stays open with what the admin typed: the instruction is saved, a retry is allowed.
          this.dialogError = failure.message || this.$t('pddDispose.refused_no_reason');
          this.dialogErrorRetry = true;
          this.notify('danger', this.$t('pddDispose.refused_title'));
          break;
        case 'validation':
          this.serverInstructionError = failure.fields.instruction_ref;
          this.serverRemarkError = failure.fields.remark;
          break;
        default:
          this.dialogError = failure.message || this.$t('pddDispose.error_dispose');
          this.notify('danger', this.$t('pddDispose.error_dispose'));
      }
    },
    stepName(step) {
      return TERMINAL_STEPS.includes(step) ? this.$t(`pddDispose.steps.${step}`) : (step || this.placeholder);
    },
    formatNumber(value) {
      if (value === null || value === undefined || value === '') return this.placeholder;
      return Math.round(Number(value) || 0).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    },
    formatMoney(value) {
      return this.formatNumber(value || 0) + ' ' + this.$t('pddDispose.kip_unit');
    },
    formatDateTime(value) {
      return value ? this.moment(value).format('DD/MM/YYYY HH:mm') : this.placeholder;
    },
    notify(type, message) {
      (type === 'success' ? notifySuccess : notifyError)({message});
    },
  },
  created() {
    this.loadList();
  },
  beforeDestroy() {
    this.alive = false;
  },
};
</script>

<style lang="scss" scoped>
.container {
  margin-top: 40px;
}

.hint {
  font-size: 12px;
  color: #777;
  margin-top: 4px;
}

// The app's global styles centre these; this page reads left to right.
.label,
.box .hint,
.section-title {
  text-align: left;
}

.mt-2 {
  margin-top: 12px;
}

.py-3 {
  padding: 1.5rem 0;
}

.section-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.section-head h5,
.section-title {
  font-size: 18px;
  font-weight: 600;
}

.state-panel {
  min-height: 140px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: #555;
}

.error-panel {
  color: #b42318;
}

.add-row {
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 12px;

  .add-field {
    flex: 1 1 280px;
  }

  .add-button {
    margin-top: 30px;
  }
}

.dispose-button {
  background: #e74c3c;
  color: #fff;

  &:hover {
    background: #c0392b;
    color: #fff;
  }
}

.card-form {
  width: 460px;
  max-width: 100%;
  text-align: left;
}

.dialog-title {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 8px;
  text-align: center;
}

.bill-box {
  font-family: monospace;
  font-size: 22px;
  font-weight: 700;
  letter-spacing: 2px;
  text-align: center;
  margin: 8px 0;
  padding: 10px;
  background: #f5f7fb;
  border-radius: 6px;
}

.field-block {
  margin-top: 12px;
}

.confirm-check {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: 12px;
  font-weight: 600;

  input {
    margin-top: 4px;
  }
}

.dialog-error {
  margin-top: 12px;
  padding: 10px 12px;
  font-size: 13px;

  .retry-note {
    margin-top: 6px;
    font-weight: 600;
  }
}

.dialog-actions {
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-top: 16px;
}
</style>
